// The book's own words for its characters, moods and locations, filtered to the ones the script
// grammar can actually spell (Basil v0.1.1, the raw-id defect).
//
// A supplied word is not a label: the decompiler PRINTS it and the parser has to read it back.
// `== intro @ Kitchen` is only correct if `Kitchen` resolves to location 5783328 again, and
// `LILLY (Happy)` is only correct if `LILLY` resolves to character 4894851 and `Happy` to
// expression 12. A writer's word is free text — it may hold spaces, punctuation, brackets, `@`,
// `(`, `→`, emoji, a leading digit — and it may collide with another word, or with the synthetic
// spelling `C123` / `E12` / `L5` the grammar reserves for an id.
//
// THE RULE, in one sentence: a word is kept only when a script written with it parses back to the
// very id it was given for, and only when no earlier id in the same domain has already claimed it
// case-insensitively; every word that fails keeps the synthetic id spelling for that id, exactly
// as the script read before any names were supplied.
//
// Two consequences worth saying out loud:
//
//  * the test is a real round trip, not a pattern match. Each candidate is decompiled into a
//    one-node probe and parsed back through this service's own `decompile` and `parse`, so the
//    rule cannot drift away from the grammar the way a hand-copied regex would.
//  * the outcome is deterministic and lossless. Candidates are considered in ascending id order,
//    the first id to claim a folded word keeps it, and a word that is refused is simply not
//    supplied — no id is ever printed with another id's word, and no id is ever lost.
//
// `variables` are passed through untouched: the memory bank has supplied them since v0.1 and their
// grammar (`> if: name`, `> remember: name`) is a different shape from these three.
import type {
  EngineId,
  Fixture,
  NameWords,
  Scene,
  SuppliedNames,
} from '../../../dorian-shared/types/episodeScript';
import { decompile } from './decompile';
import { parse } from './parse';

/** The three domains a word is printed into the script for. */
export type SpellableDomain = 'characters' | 'expressions' | 'locations';

/** Why a supplied word was not used. */
export type SpellableRefusal =
  /** not a non-empty string, or it carries a line break. */
  | 'unusable'
  /** the script written with it did not read back as this id. */
  | 'not-spellable'
  /** an earlier id in the same domain already claimed the same word, case-insensitively. */
  | 'duplicate';

/** One refused word, for a test or a log. Nothing here is ever shown to a writer. */
export interface SpellableDrop {
  id: string;
  name: string;
  refusal: SpellableRefusal;
}

/** What `spellableNames` decided for one domain. */
export interface SpellableResult {
  /** the words that may be supplied: id → word. */
  kept: Record<string, string>;
  /** the words that may not, and why. */
  dropped: SpellableDrop[];
}

/** The ids the probe document uses. They are local to the probe and never leave this file. */
const PROBE_BRANCH_ID = 1;
const PROBE_STEP_ID = 1;
/** The speaker a mood is probed under: unnamed, so it prints as its own synthetic spelling. */
const PROBE_SPEAKER_ID = 1;

/** id → word from either supported shape, as plain string pairs. */
function pairs(words: NameWords | undefined): [string, string][] {
  const entries = words instanceof Map ? Array.from(words.entries()) : Object.entries(words ?? {});

  return entries.map(([id, name]): [string, string] => [String(id), String(name ?? '')]);
}

/** Ascending id order: numeric where both ids are numbers, and by their spelling otherwise. */
function byId(left: [string, string], right: [string, string]): number {
  const a = Number(left[0]);
  const b = Number(right[0]);

  if (Number.isFinite(a) && Number.isFinite(b) && a !== b) {
    return a - b;
  }
  if (left[0] === right[0]) {
    return 0;
  }

  return left[0] < right[0] ? -1 : 1;
}

/** The one-node document a candidate is printed into: a scene, a speaker, a mood and a line. */
function probeFixture(domain: SpellableDomain, id: EngineId): Fixture {
  return {
    branches: [{
      id: PROBE_BRANCH_ID,
      title: 'Scene',
      locationId: domain === 'locations' ? id : null,
      steps: [{
        id: PROBE_STEP_ID,
        branchId: PROBE_BRANCH_ID,
        stepTypeId: 1,
        text: 'a line',
        characterId: domain === 'characters' ? id : PROBE_SPEAKER_ID,
        characterExpressionId: domain === 'expressions' ? id : null,
      }],
    }],
  };
}

/** The id the probe's script read back for this domain, as a string; `''` when it read back none. */
function readBack(domain: SpellableDomain, scene: Scene | undefined): string {
  if (!scene) {
    return '';
  }
  if (domain === 'locations') {
    return scene.locationId == null ? '' : String(scene.locationId);
  }
  const spoken = scene.blocks.find((block) => block.type === 'character');

  if (!spoken || spoken.type !== 'character') {
    return '';
  }
  if (domain === 'characters') {
    return spoken.characterId == null ? '' : String(spoken.characterId);
  }

  return spoken.characterExpressionId == null ? '' : String(spoken.characterExpressionId);
}

/**
 * True when a script written with `name` for `id` parses back to `id`.
 *
 * The probe goes through this service's own decompiler and parser, with the names table the
 * decompiler built, so it measures the grammar rather than describing it.
 */
export function spellsBackToItsId(domain: SpellableDomain, id: EngineId, name: string): boolean {
  const word = String(name ?? '');

  if (word === '' || /[\r\n]/.test(word)) {
    return false;
  }
  const supplied: SuppliedNames = { [domain]: { [String(id)]: word } };

  try {
    const written = decompile(probeFixture(domain, id), { names: supplied });
    const read = parse(written.text, { names: written.names });

    return readBack(domain, read.scenes[0]) === String(id);
  } catch {
    return false;
  }
}

/**
 * One domain's supplied words, filtered to the ones the script can spell.
 *
 * Ids are considered in ascending order and the first to claim a folded word keeps it, so the same
 * book always produces the same script whatever order the engine listed its rows in.
 */
export function spellableNames(domain: SpellableDomain, words: NameWords | undefined): SpellableResult {
  const kept: Record<string, string> = {};
  const dropped: SpellableDrop[] = [];
  const claimed = new Set<string>();

  pairs(words).sort(byId).forEach(([id, name]) => {
    if (name.trim() === '') {
      dropped.push({ id, name, refusal: 'unusable' });

      return;
    }
    const fold = name.toLocaleLowerCase();

    if (claimed.has(fold)) {
      dropped.push({ id, name, refusal: 'duplicate' });

      return;
    }
    if (!spellsBackToItsId(domain, id, name)) {
      dropped.push({ id, name, refusal: 'not-spellable' });

      return;
    }
    claimed.add(fold);
    kept[id] = name;
  });

  return { kept, dropped };
}

/**
 * `supplied` with its three printed domains filtered by the rule above; `variables` untouched.
 *
 * A domain that is absent stays absent, so a caller that could not read one of the three supplies
 * nothing for it and the script keeps that domain's ids — the fail-open behaviour the read lane
 * depends on.
 */
export function sanitiseSuppliedNames(supplied: SuppliedNames | null | undefined): SuppliedNames {
  const source = supplied ?? {};
  const result: SuppliedNames = {};
  const domains: SpellableDomain[] = ['characters', 'expressions', 'locations'];

  domains.forEach((domain) => {
    if (source[domain] === undefined) {
      return;
    }
    result[domain] = spellableNames(domain, source[domain]).kept;
  });
  if (source.variables !== undefined) {
    result.variables = source.variables;
  }

  return result;
}
